
import { useCallback } from 'react';
import { useAdminActions } from './useAdminActions';
import { toast } from 'sonner';
import { EnrollmentEvent } from '@/services/db/enrollment/types';
import { getStudentEnrollmentHistory } from '@/services/db/enrollment/enrollmentHistoryService';

export const useActionTracking = (entityId: string = 'system_settings') => {
  const { logStudentModification, logDocumentAction, logPermissionChange, logSystemSettingChange } = useAdminActions(entityId);

  const trackStudentAction = useCallback((
    studentId: number,
    username: string = 'Administrator',
    action: string,
    details: Record<string, any> = {}
  ) => {
    try {
      // Log action to admin tracking system
      logStudentModification(
        studentId,
        username,
        details,
        action
      );
      
      console.log(`Tracked student action: ${action} for student ID ${studentId}`);
      return true;
    } catch (error) {
      console.error('Failed to track student action:', error);
      return false;
    }
  }, [logStudentModification]);

  const getStudentHistory = useCallback(async (studentId: number): Promise<EnrollmentEvent[]> => {
    try {
      const history = await getStudentEnrollmentHistory(studentId);
      console.log(`Retrieved ${history.length} history records for student ID ${studentId}`, history);
      return history;
    } catch (error) {
      console.error('Failed to get student history:', error);
      return [];
    }
  }, []);

  const trackDocumentAction = useCallback((
    documentId: string,
    status: 'uploaded' | 'approved' | 'rejected',
    username: string = 'Administrator',
    details: Record<string, any> = {}
  ) => {
    try {
      logDocumentAction(
        documentId,
        status,
        username,
        details
      );
    } catch (error) {
      console.error('Failed to track document action:', error);
    }
  }, [logDocumentAction]);

  const trackPermissionAction = useCallback((
    userId: string,
    username: string = 'Administrator',
    oldPermissions: string[],
    newPermissions: string[]
  ) => {
    try {
      logPermissionChange(
        userId,
        username,
        oldPermissions,
        newPermissions
      );
    } catch (error) {
      console.error('Failed to track permission action:', error);
    }
  }, [logPermissionChange]);

  const trackSettingChange = useCallback((
    settingKey: string,
    username: string = 'Administrator',
    oldValue: any,
    newValue: any
  ) => {
    try {
      logSystemSettingChange(
        settingKey,
        username,
        oldValue,
        newValue
      );
    } catch (error) {
      console.error('Failed to track setting change:', error);
    }
  }, [logSystemSettingChange]);

  return {
    trackStudentAction,
    getStudentHistory,
    trackDocumentAction,
    trackPermissionAction,
    trackSettingChange
  };
};
